/* @jsx h */
import { BottomSheet } from "../BottomSheet";
import { FullScreenSheet } from "../FullScreenSheet";
import {
  defineComponent,
  ref,
  onMounted,
  h,
  watch,
  nextTick,
  VNode,
  Teleport,
} from "vue";

export type RightSheetMobileVariant = "bottom-sheet" | "full-screen";

export type RightSheetProps = {
  isOpen: boolean;
  title?: string | VNode;
  onClose: Function;
  width?: string;
  zIndex?: string;
  disableBottomSheet?: boolean;
  style?: string;
  class?: string;
  height?: string;
  testId?: string;
  /** How the sheet degrades on mobile. Defaults to "bottom-sheet". */
  mobileVariant?: RightSheetMobileVariant;
  /** Hide the back arrow of the mobile full-screen variant (e.g. once leaving
   * the screen is no longer a valid action). */
  hideBack?: boolean;
};

export const RightSheet = defineComponent<RightSheetProps>({
  name: "RightSheet",
  props: [
    "isOpen",
    "title",
    "onClose",
    "width",
    "zIndex",
    "disableBottomSheet",
    "style",
    "class",
    "height",
    "testId",
    "mobileVariant",
    "hideBack",
  ],
  setup(props, { slots }) {
    const sheetRef = ref<HTMLDivElement | null>(null);
    const scrollContentRef = ref<HTMLDivElement | null>(null);
    const startX = ref(0);
    const isDragging = ref(false);
    const draggingOffset = ref(0);
    const show = ref(false);

    const transitionIn = () => {
      show.value = true;
      nextTick(() => {
        requestAnimationFrame(() => {
          if (sheetRef.value) {
            sheetRef.value.style.transition = "transform 0ms ease-out";
            sheetRef.value.style.transform = `translate3d(0, 0, 0)`;
          }
        });
      });
    };

    const transitionOut = () => {
      if (sheetRef.value) {
        sheetRef.value.style.transition = "transform 0ms ease-out";
        sheetRef.value.style.transform = `translate3d(100%, 0, 0)`;

        setTimeout(() => {
          show.value = false;
          props.onClose?.();
        }, 250);
      }
    };

    const startY = ref(0);
    const gestureQualified = ref(false);

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      startX.value = touch.clientX;
      startY.value = touch.clientY;
      isDragging.value = true;
      gestureQualified.value = false;
      draggingOffset.value = 0;

      if (sheetRef.value) {
        sheetRef.value.style.transition = "none";
        sheetRef.value.style.willChange = "transform";
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging.value || !sheetRef.value) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - startX.value;
      const deltaY = Math.abs(touch.clientY - startY.value);

      if (deltaX < 0) return; // ignore leftward

      if (!gestureQualified.value) {
        if (deltaX > deltaY && deltaX > 20) {
          gestureQualified.value = true;
        } else {
          return; // don't prevent scroll
        }
      }

      e.preventDefault(); // now safe to prevent scroll
      draggingOffset.value = deltaX;
      sheetRef.value.style.transform = `translate3d(${deltaX}px, 0, 0)`;
    };

    const handleTouchEnd = () => {
      if (!isDragging.value || !sheetRef.value) return;

      isDragging.value = false;

      if (gestureQualified.value && draggingOffset.value > 120) {
        transitionOut();
      } else {
        sheetRef.value.style.transition = "transform 0ms ease-out";
        sheetRef.value.style.transform = `translate3d(0, 0, 0)`;
      }

      draggingOffset.value = 0;
      gestureQualified.value = false;
    };

    watch(
      () => props.isOpen,
      (newVal) => {
        if (newVal) transitionIn();
        else transitionOut();
      },
    );

    onMounted(() => {
      if (props.isOpen) transitionIn();
    });

    return () => {
      if (!props.disableBottomSheet && window.innerWidth < 600) {
        if (props.mobileVariant === "full-screen") {
          return (
            <FullScreenSheet
              isOpen={true}
              onClose={() => props.onClose()}
              class={props.class}
              animation="slide-up"
            >
              <div
                class="flex h-full min-h-0 flex-col bg-base-100"
                style={{ paddingTop: "var(--safe-area-top, 0px)" }}
              >
                <div class="flex items-center px-4 py-3 border-b shrink-0">
                  {!props.hideBack && (
                    <button
                      class="btn btn-ghost btn-sm btn-circle mr-2 shrink-0"
                      onClick={() => props.onClose?.()}
                    >
                      <span class="material-symbols-outlined">arrow_back</span>
                    </button>
                  )}
                  <div class="flex-1 min-w-0">
                    {props.title ? (
                      typeof props.title === "string" ? (
                        <h2 class="text-base font-semibold text-accent truncate">
                          {props.title}
                        </h2>
                      ) : (
                        props.title
                      )
                    ) : null}
                  </div>
                </div>
                <div class="flex flex-1 min-h-0 flex-col overflow-hidden">
                  {slots.default?.()}
                </div>
              </div>
            </FullScreenSheet>
          );
        }

        return (
          <BottomSheet
            title={props.title}
            isOpen={true}
            onClose={() => props.onClose()}
            class={props.class}
            height={props.height}
          >
            <div class="flex flex-1 min-h-0 flex-col overflow-hidden">
              <div class="flex flex-1 min-h-0 flex-col overflow-hidden">
                {slots.default?.()}
              </div>
            </div>
          </BottomSheet>
        );
      }

      return (
        <Teleport to="body">
          <div
            class={"fixed bg-black/20  top-0 bottom-0 left-0 right-0 my-0"}
            onClick={() => props.onClose}
            style={{
              display: show.value ? "block" : "none",
              background: "oklch(var(--b1)/ 0.6)",
            }}
          >
            <div
              ref={sheetRef}
              onTouchstart={handleTouchStart}
              onTouchmove={handleTouchMove}
              onTouchend={handleTouchEnd}
              onClick={(e) => e.stopPropagation()}
              class="absolute top-4 bottom-4 right-4 glossy-bg shadow-xl overflow-hidden rounded rounded-xl border l-box-shadow"
              data-testid={props.testId}
              style={`
              height: ${props.height || "auto"};
                  transform: translate3d(100%, 0, 0);
                  will-change: transform;
                  width: ${props.width || "600px"};
                  transition: transform 0ms ease-out;
                  display: flex;
                  flex-direction: column;
                  ${props.style || ""}
                `}
            >
              <div class="flex justify-between items-center p-2 p-4 border-b  ">
                {props.title ? (
                  typeof props.title === "string" ? (
                    <h2 class="text-2xl text-accent">{props.title}</h2>
                  ) : (
                    props.title
                  )
                ) : (
                  <div class="flex-1"></div>
                )}
                <button
                  class="btn btn-circle btn-sm"
                  onClick={() => props.onClose?.()}
                >
                  <span class="material-symbols-outlined">close</span>
                </button>
              </div>
              <div
                ref={scrollContentRef}
                class="overflow-y-auto flex flex-col flex-1 p-0"
                style="-webkit-overflow-scrolling: touch;"
              >
                {slots.default?.()}
              </div>
            </div>
          </div>
        </Teleport>
      );
    };
  },
});
